import { useState, type ComponentType } from "react";
import { Outlet, useNavigate } from "react-router";
import {
  Bell,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Home,
  Inbox,
  LogOut,
  Menu,
  Search,
  Settings,
  Target,
  Users,
} from "lucide-react";

import { canCreateProperty, canManageTeam } from "@/features/settings/role-labels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { MobileTabBar, type MobileTabItem, SidebarNav, type SidebarNavItem } from "@/components/estate";
import {
  CommandPaletteProvider,
  useCommandPalette,
} from "@/components/command-palette";
import { apiRequest } from "@/lib/api-client";
import { useAuthStore, type AuthUser } from "@/lib/auth-store";
import { useDashboardSummary } from "@/features/dashboard/api";
import { useCustomers } from "@/features/customer/api";
import { useViewingInbox } from "@/features/appointment/api";
import { useUnreadCount } from "@/features/notification/api";
import { NotificationBell } from "@/features/notification/notification-bell";

// The three always-visible "doing" hubs — listings, leads, calendar —
// arranged 2 + FAB + 2 with the Menu tab. Everything else (dashboard,
// notifications, customers, inbox, import, settings) lives in the "Menu"
// sheet so the mobile IA covers everything the desktop sidebar does —
// the review's "same mental model on both platforms". Tổng quan sits in
// the sheet: login already lands on BĐS, and the dashboard is a glance
// screen, not a doing screen.
const BASE_MOBILE_TABS: MobileTabItem[] = [
  {
    key: "props",
    label: "BĐS",
    href: "/properties",
    icon: Building2,
    matchPaths: ["/properties"],
  },
  {
    key: "leads",
    label: "Pipeline",
    href: "/leads",
    icon: Target,
    matchPaths: ["/leads", "/customers"],
  },
  {
    key: "cal",
    label: "Lịch",
    href: "/appointments",
    icon: Calendar,
    matchPaths: ["/appointments"],
  },
];

/**
 * Layout that wraps every authenticated route with the command-palette
 * context — used as the parent of both `AppShell` and `NoShellLayout`
 * so ⌘K works regardless of whether the page has the global nav.
 */
export function AuthenticatedLayout() {
  return (
    <CommandPaletteProvider>
      <Outlet />
    </CommandPaletteProvider>
  );
}

/**
 * Standard authenticated chrome: desktop sidebar + mobile tab bar +
 * page content. Used for list / dashboard / settings routes.
 */
export function AppShell() {
  const role = useAuthStore((s) => s.user?.role);
  const [moreOpen, setMoreOpen] = useState(false);
  // The menu tab's bubble = everything actionable hiding behind it:
  // unread notifications + viewings awaiting my response.
  const unread = useUnreadCount().data ?? 0;
  const inboxCount = useViewingInbox("me").data?.length ?? 0;

  const tabs: MobileTabItem[] = [
    ...BASE_MOBILE_TABS,
    {
      key: "more",
      label: "Menu",
      icon: Menu,
      onSelect: () => setMoreOpen(true),
      ...(unread + inboxCount > 0 ? { badge: unread + inboxCount } : {}),
    },
  ];

  return (
    <div className="grid h-full min-h-svh md:grid-cols-[232px_1fr]">
      <DesktopSidebar />
      {/* Cap the content to ~1600px, centered, on wide screens. The layout is
          laptop-tuned (no breakpoint above lg), so past this width it would just
          inflate — cards balloon rather than show more — and read as "lost" on a
          27" monitor. Capping keeps one consistent density from a 14" laptop up.
          Applied to every sidebar layout so pages don't disagree about width. */}
      <main className="mx-auto flex w-full min-h-svh max-w-[1600px] flex-col overflow-x-hidden pb-[calc(env(safe-area-inset-bottom)+88px)] md:pb-0">
        <Outlet />
      </main>
      <MobileTabBar
        items={tabs}
        {...(canCreateProperty(role) ? { fabHref: "/properties/new" } : {})}
      />
      <MobileMoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        unread={unread}
        inboxCount={inboxCount}
      />
    </div>
  );
}

/**
 * Shell-less layout for "deep" detail routes (property/customer detail).
 * No sidebar, no tab bar, no bottom safe-area padding — the page handles
 * its own back-button + breadcrumb chrome, and any sticky bottom CTA
 * (e.g. `Đặt lịch xem`) gets the full screen edge.
 *
 * Reasoning matches the design comp: detail pages are arrived at from a
 * parent list and users almost always want to return there. A back chevron
 * is more useful than the global nav. Real estate is photo-heavy — full
 * bleed heroes + sticky action bars need the screen edges.
 */
export function NoShellLayout() {
  return (
    <main className="flex min-h-svh flex-col overflow-x-hidden">
      <Outlet />
    </main>
  );
}

/**
 * Desktop sidebar + content, but NO mobile tab bar. For deep detail pages that
 * want the global nav on desktop yet stay full-bleed on mobile (their own back
 * button + sticky bottom CTA). On mobile the sidebar is hidden (`md:flex`) and
 * no tab bar renders, so the page owns the full screen — same as NoShellLayout.
 */
export function DetailShellLayout() {
  return (
    <div className="grid h-full min-h-svh md:grid-cols-[232px_1fr]">
      <DesktopSidebar />
      {/* Same wide-screen content cap as AppShell (see there). */}
      <main className="mx-auto flex w-full min-h-svh max-w-[1600px] flex-col overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}

function DesktopSidebar() {
  const { setOpen } = useCommandPalette();
  const user = useAuthStore((s) => s.user);
  const items = useSidebarItems();
  return (
    <aside className="hidden h-svh flex-col gap-1 overflow-hidden border-r border-border bg-card px-3.5 py-5 md:flex">
      <div className="flex items-start justify-between">
        <BrandMark />
        <NotificationBell />
      </div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mx-1 mb-3 flex items-center gap-2 rounded-lg bg-muted px-2.5 py-1.5 text-left text-[12px] text-muted-foreground transition-colors hover:bg-muted/80"
      >
        <Search className="size-3.5" />
        <span className="flex-1">Tìm kiếm…</span>
        <kbd className="rounded border border-border bg-card px-1.5 py-px font-mono text-[11px] text-ink-faint">
          ⌘K
        </kbd>
      </button>
      <SidebarNav items={items} />
      <Separator className="my-3" />
      <SidebarNav
        items={[{ label: "Cài đặt", href: "/settings", icon: Settings }]}
      />
      <div className="mt-auto">
        <UserCard user={user} />
      </div>
    </aside>
  );
}

// Sidebar item counts come from the dashboard summary endpoint (owners/
// managers only) plus a 1-item customer list query for the contact count.
// Both queries fail-soft for users without access — the sidebar simply
// renders without counts rather than blowing up.
function useSidebarItems(): SidebarNavItem[] {
  const summary = useDashboardSummary();
  const customers = useCustomers({ page: 0, size: 1 });
  const inbox = useViewingInbox("me");
  const role = useAuthStore((s) => s.user?.role);
  const propsCount = summary.data?.properties.total;
  const customersCount = customers.data?.totalItems;
  const leadsCount = summary.data?.leads.total;
  const newLeads = summary.data?.leads.byStatus.NEW ?? 0;
  const upcomingAppts = summary.data?.appointments.upcoming7d;
  const inboxCount = inbox.data?.length;

  return [
    { label: "Tổng quan", href: "/dashboard", icon: Home, end: false },
    {
      label: "Bất động sản",
      href: "/properties",
      icon: Building2,
      // Don't activate "Bất động sản" when the user is on /properties/import,
      // because that path has its own sidebar entry below. Property detail
      // pages (/properties/123) still highlight this item via the default
      // prefix match.
      excludePaths: ["/properties/import"],
      ...(propsCount !== undefined ? { count: propsCount } : {}),
    },
    ...(canCreateProperty(role)
      ? ([
          {
            // Lives directly under "Bất động sản" — a property-management task.
            // Only roles that can create properties see the importer.
            label: "Nhập Excel",
            href: "/properties/import",
            icon: FileSpreadsheet,
          },
        ] satisfies SidebarNavItem[])
      : []),
    {
      label: "Khách hàng",
      href: "/customers",
      icon: Users,
      ...(customersCount !== undefined ? { count: customersCount } : {}),
    },
    {
      label: "Khách tiềm năng",
      href: "/leads",
      icon: Target,
      ...(leadsCount !== undefined ? { count: leadsCount } : {}),
      ...(newLeads > 0 ? { alert: newLeads } : {}),
    },
    {
      label: "Lịch hẹn",
      href: "/appointments",
      icon: Calendar,
      ...(upcomingAppts !== undefined ? { count: upcomingAppts } : {}),
    },
    {
      label: "Cần tôi xử lý",
      href: "/inbox",
      icon: Inbox,
      // Viewings awaiting my response — actionable, so use the emphasized
      // badge when non-zero (every sibling shows a count; this one was the
      // odd item out).
      ...(inboxCount !== undefined ? { count: inboxCount } : {}),
      ...(inboxCount !== undefined && inboxCount > 0 ? { alert: inboxCount } : {}),
    },
  ];
}

function BrandMark() {
  return (
    <div className="flex items-center gap-2.5 px-2 pb-5">
      <div className="flex size-[30px] items-center justify-center rounded-lg bg-gradient-to-br from-primary to-foreground text-sm font-bold text-primary-foreground">
        E
      </div>
      <div className="leading-tight">
        <div className="text-[16px] font-bold tracking-tight">Estate</div>
        <div className="-mt-0.5 text-[11px] text-muted-foreground">
          realty workspace
        </div>
      </div>
    </div>
  );
}

/** Best-effort server logout, then clear local auth and return to /login. */
function useLogout() {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  return async () => {
    try {
      await apiRequest<void>("/auth/logout", { method: "POST" });
    } catch {
      // Best-effort logout — clear local state regardless.
    }
    clearAuth();
    navigate("/login", { replace: true });
  };
}

/**
 * Mobile "Menu" sheet — the secondary destinations that don't fit the tab
 * bar. Mirrors the desktop sidebar's full IA (notifications, customers,
 * inbox, import, settings) so nothing is desktop-only, and gives Settings
 * a labeled home on mobile instead of an unlabeled avatar tap.
 */
function MobileMoreSheet({
  open,
  onOpenChange,
  unread,
  inboxCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unread: number;
  inboxCount: number;
}) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();

  const go = (href: string) => {
    onOpenChange(false);
    navigate(href);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-4 md:hidden"
      >
        <SheetTitle className="sr-only">Menu</SheetTitle>

        <div className="flex items-center gap-2.5 rounded-xl bg-muted px-3 py-2.5">
          <div className="flex size-[34px] items-center justify-center rounded-lg bg-foreground font-mono text-[11px] font-semibold text-background">
            {computeInitials(user)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[14px] font-semibold">
              {user?.fullName?.trim() || user?.email?.split("@")[0] || "Người dùng"}
            </div>
            <div className="truncate text-[11px] text-muted-foreground">
              {roleToLabel(user?.role)}
            </div>
          </div>
        </div>

        <nav className="mt-3 flex flex-col">
          <MoreSheetItem
            icon={Home}
            label="Tổng quan"
            onClick={() => go("/dashboard")}
          />
          <MoreSheetItem
            icon={Bell}
            label="Thông báo"
            badge={unread}
            onClick={() => go("/notifications")}
          />
          <MoreSheetItem
            icon={Inbox}
            label="Cần tôi xử lý"
            badge={inboxCount}
            onClick={() => go("/inbox")}
          />
          <MoreSheetItem
            icon={Users}
            label="Khách hàng"
            onClick={() => go("/customers")}
          />
          {canCreateProperty(user?.role) ? (
            <MoreSheetItem
              icon={FileSpreadsheet}
              label="Nhập Excel"
              onClick={() => go("/properties/import")}
            />
          ) : null}
          <MoreSheetItem
            icon={Settings}
            label="Cài đặt"
            onClick={() =>
              go(canManageTeam(user?.role) ? "/settings?tab=team" : "/settings?tab=org")
            }
          />
        </nav>

        <Separator className="my-2" />
        <button
          type="button"
          onClick={() => void logout()}
          className="flex h-11 w-full items-center gap-3 rounded-lg px-2 text-left text-[14px] font-medium text-destructive transition-colors hover:bg-destructive-soft"
        >
          <LogOut className="size-[18px]" aria-hidden="true" />
          Đăng xuất
        </button>
      </SheetContent>
    </Sheet>
  );
}

function MoreSheetItem({
  icon: Icon,
  label,
  badge,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  badge?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-11 w-full items-center gap-3 rounded-lg px-2 text-left text-[14px] font-medium text-foreground transition-colors hover:bg-muted"
    >
      <Icon className="size-[18px] text-ink-soft" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 ? (
        <span className="inline-flex min-w-5 items-center justify-center rounded-md bg-primary px-1.5 py-0.5 font-mono text-[11px] font-semibold leading-none text-primary-foreground">
          {badge}
        </span>
      ) : null}
      <ChevronRight className="size-4 text-ink-faint" aria-hidden="true" />
    </button>
  );
}

function UserCard({ user }: { user: AuthUser | null }) {
  const navigate = useNavigate();
  const handleLogout = useLogout();

  const initials = computeInitials(user);
  const displayName = user?.fullName?.trim() || user?.email?.split("@")[0] || "Người dùng";
  const roleLabel = roleToLabel(user?.role);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-xl bg-muted px-2.5 py-2 text-left transition-colors hover:bg-muted/80"
        >
          <div className="flex size-[30px] items-center justify-center rounded-lg bg-foreground font-mono text-[11px] font-semibold text-background">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] font-semibold">{displayName}</div>
            <div className="truncate text-[11px] text-muted-foreground">{roleLabel}</div>
          </div>
          <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="max-w-[14rem] truncate text-xs">
          {user?.email ?? "Người dùng"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() =>
            navigate(
              canManageTeam(user?.role) ? "/settings?tab=team" : "/settings?tab=org",
            )
          }
        >
          <Settings aria-hidden="true" />
          Cài đặt
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut aria-hidden="true" />
          Đăng xuất
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function computeInitials(user: AuthUser | null): string {
  if (user?.fullName?.trim()) {
    const words = user.fullName.trim().split(/\s+/);
    const first = words[0]?.[0];
    const last = words[words.length - 1]?.[0];
    if (first && last && words.length >= 2) {
      return (first + last).toUpperCase();
    }
    return (words[0] ?? "").slice(0, 2).toUpperCase() || "?";
  }
  if (user?.email) {
    const local = user.email.split("@")[0] ?? "";
    const parts = local.split(/[._-]/).filter(Boolean);
    const a = parts[0]?.[0];
    const b = parts[1]?.[0];
    if (a && b) {
      return (a + b).toUpperCase();
    }
    return local.slice(0, 2).toUpperCase() || "?";
  }
  return "?";
}

function roleToLabel(role: string | undefined): string {
  switch (role) {
    case "ADMIN":
      return "Quản trị viên";
    case "MANAGER":
      return "Quản lý";
    case "BROKER":
      return "Môi giới";
    default:
      return role ? role : "Thành viên";
  }
}

